/**
 * ENG-5516: parse per-message token usage out of a Claude Code session
 * transcript (the `~/.claude/projects/<encoded-cwd>/<sessionId>.jsonl` file).
 *
 * Each line of the transcript is a JSON object. Assistant turns look like:
 *
 *   {
 *     "type": "assistant",
 *     "timestamp": "2026-05-25T01:02:03.456Z",
 *     "message": {
 *       "id": "msg_01ABC...",
 *       "model": "claude-opus-4-7",
 *       "usage": {
 *         "input_tokens": 4,
 *         "output_tokens": 312,
 *         "cache_creation_input_tokens": 1024,
 *         "cache_read_input_tokens": 18000
 *       }
 *     }
 *   }
 *
 * We sum the four usage fields across every distinct assistant message,
 * grouped by model. The totals are CUMULATIVE for the whole transcript —
 * the manager re-reads the file each flush and upserts these totals, so the
 * write path is idempotent (re-reading yields the same numbers). That is why
 * we don't track byte offsets or deltas.
 *
 * Defensive by design: the JSONL layout is an undocumented Claude Code
 * internal that has drifted across versions (see daily-session.ts ENG-4659
 * for a live incident caused by exactly this kind of drift). Malformed lines,
 * missing fields, and unknown line types are skipped rather than thrown — a
 * parse failure must degrade to an under-count, never crash the manager.
 *
 * Dedupe: a streamed/rewritten turn can emit the same `message.id` more than
 * once with successive usage snapshots. We keep the LAST usage seen per id so
 * the final authoritative count wins and intermediate snapshots don't
 * double-count. Lines without a `message.id` can't be deduped, so each is
 * counted once under a synthetic key.
 *
 * ── `usage.iterations[]` (ENG-10108) ────────────────────────────────────────
 *
 * `usage` may carry an `iterations` array describing the sub-calls that made up
 * the turn. Two shapes exist, and which one you have is told by whether the
 * iteration declares its own `model`:
 *
 *   { "type": "message",          ...counters }                  // no `model`
 *   { "type": "advisor_message",  "model": "claude-fable-5", ... } // has one
 *
 * **The outer `usage` is exactly the sum of the model-LESS iterations.**
 * Measured over 275,351 iteration-bearing messages in 233 transcripts: the
 * invariant held on all four counters 275,348 times. A worked example —
 *
 *   outer                 in 4      out 547   read 124,603  create 4,414
 *   iter `message`        in 2      out 374   read  60,725  create 3,153
 *   iter `advisor_message` in 65,558 out 2,689 read      0  create     0
 *   iter `message`        in 2      out 173   read  63,878  create 1,261
 *
 * — rows 1 and 3 reconstruct the outer row column by column; row 2 is absent
 * from it. So adding a model-less iteration DOUBLE-COUNTS, and in the 99.1%
 * single-iteration case it double-counts the whole message.
 *
 * A model-BEARING iteration is spend the outer row does not contain, on a
 * different rate card, and before ENG-10108 it was counted nowhere at all.
 *
 * We therefore split each message into per-model contributions: the outer usage
 * under `message.model`, plus one contribution per iteration that declares a
 * model. The rule keys on `model` presence, never on a `type` string — an
 * iteration with no model cannot be attributed to another model anyway, and a
 * future model-bearing iteration type is picked up without a code change.
 *
 * Known floor, deliberately not fixed here: 3 of those 275,351 messages (three
 * snapshots of one message) carry an all-zero outer `usage` beside a populated
 * model-less iteration. Capturing those would mean REPLACING the outer totals
 * with the iteration sum rather than adding to them — a much larger behaviour
 * change, on evidence of a single message.
 */

import { RUN_MARKER_RE } from './run-marker.js';

export interface TranscriptUsageTotals {
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
}

export interface TranscriptParseResult {
  /** Cumulative totals per model id (keyed by `message.model`). */
  byModel: Map<string, TranscriptUsageTotals>;
  /** Earliest assistant `timestamp` seen (ISO 8601), or null if none. */
  sessionStartedAt: string | null;
  /** Latest assistant `timestamp` seen (ISO 8601), or null if none. */
  lastObservedAt: string | null;
  /** Number of distinct assistant messages counted (after dedupe). */
  messageCount: number;
}

/** Coerce an unknown JSON value to a non-negative integer; anything invalid → 0. */
function nonNegInt(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  const floored = Math.floor(value);
  return floored > 0 ? floored : 0;
}

function emptyTotals(): TranscriptUsageTotals {
  return { inputTokens: 0, outputTokens: 0, cacheCreationTokens: 0, cacheReadTokens: 0 };
}

/** Read the four token counters out of a `usage`-shaped object. */
function readUsageTotals(u: Record<string, unknown>): TranscriptUsageTotals {
  return {
    inputTokens: nonNegInt(u.input_tokens),
    outputTokens: nonNegInt(u.output_tokens),
    cacheCreationTokens: nonNegInt(u.cache_creation_input_tokens),
    cacheReadTokens: nonNegInt(u.cache_read_input_tokens),
  };
}

/** One model's share of a single assistant message's usage. */
interface UsageContribution {
  model: string;
  totals: TranscriptUsageTotals;
}

/**
 * ENG-10108: split one assistant message's usage into per-model contributions —
 * the outer `usage` under `message.model`, plus every `usage.iterations[]` entry
 * that declares its own `model`.
 *
 * See the module docstring for why `model` presence is the discriminator and
 * why model-less iterations must NOT be added. Two subtleties:
 *
 *  - It tests PRESENCE, not difference from the outer model. An advisor
 *    configured on the same model as the main agent still spends separately, so
 *    `it.model !== msg.model` would silently drop it. Same-model contributions
 *    simply fold together downstream, which is correct.
 *  - The outer contribution is emitted even when its counters are all zero, so
 *    `messageCount` and the model keyspace are unchanged for every message that
 *    has no iterations — this is additive, not a rewrite of existing behaviour.
 */
function usageContributions(
  msg: Record<string, unknown>,
  u: Record<string, unknown>,
): UsageContribution[] {
  const model = typeof msg.model === 'string' && msg.model ? msg.model : 'unknown';
  const out: UsageContribution[] = [{ model, totals: readUsageTotals(u) }];

  const iterations = u.iterations;
  if (Array.isArray(iterations)) {
    for (const it of iterations) {
      if (typeof it !== 'object' || it === null) continue;
      const iter = it as Record<string, unknown>;
      // No own model → already inside the outer totals. Skip it.
      if (typeof iter.model !== 'string' || !iter.model) continue;
      out.push({ model: iter.model, totals: readUsageTotals(iter) });
    }
  }
  return out;
}

/**
 * Parse the full text of a transcript JSONL and return cumulative per-model
 * token totals plus the session's first/last timestamps.
 */
export function parseTranscriptUsage(jsonl: string): TranscriptParseResult {
  // message.id → its latest per-model contributions. Synthetic keys for id-less
  // lines. A message contributes more than one entry when it carries a
  // model-bearing `usage.iterations[]` (ENG-10108).
  const byId = new Map<string, UsageContribution[]>();
  let sessionStartedAt: string | null = null;
  let lastObservedAt: string | null = null;
  let syntheticCounter = 0;

  const lines = jsonl.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    let obj: unknown;
    try {
      obj = JSON.parse(trimmed);
    } catch {
      // Malformed (e.g. a partial trailing line mid-write) — skip.
      continue;
    }

    if (typeof obj !== 'object' || obj === null) continue;
    const record = obj as Record<string, unknown>;
    if (record.type !== 'assistant') continue;

    const message = record.message;
    if (typeof message !== 'object' || message === null) continue;
    const msg = message as Record<string, unknown>;

    const usage = msg.usage;
    if (typeof usage !== 'object' || usage === null) continue;
    const u = usage as Record<string, unknown>;

    // Dedupe by message.id (last write wins); fall back to a synthetic key. The
    // last snapshot carries the complete `iterations` array, so replacing the
    // whole contribution list per id keeps the advisor row in step with the
    // outer row rather than accumulating partial snapshots of either.
    const id =
      typeof msg.id === 'string' && msg.id ? msg.id : `__noid_${syntheticCounter++}`;
    byId.set(id, usageContributions(msg, u));

    // Track session time bounds from the top-level ISO timestamp.
    const ts = record.timestamp;
    if (typeof ts === 'string' && ts) {
      if (sessionStartedAt === null || ts < sessionStartedAt) sessionStartedAt = ts;
      if (lastObservedAt === null || ts > lastObservedAt) lastObservedAt = ts;
    }
  }

  const byModel = new Map<string, TranscriptUsageTotals>();
  for (const contributions of byId.values()) {
    for (const { model, totals } of contributions) {
      const acc = byModel.get(model) ?? emptyTotals();
      acc.inputTokens += totals.inputTokens;
      acc.outputTokens += totals.outputTokens;
      acc.cacheCreationTokens += totals.cacheCreationTokens;
      acc.cacheReadTokens += totals.cacheReadTokens;
      byModel.set(model, acc);
    }
  }

  return {
    byModel,
    sessionStartedAt,
    lastObservedAt,
    messageCount: byId.size,
  };
}

export interface WindowedUsageResult {
  /** Cumulative totals per model id, for messages inside the window. */
  byModel: Map<string, TranscriptUsageTotals>;
  /** Sum across all models inside the window. */
  totals: TranscriptUsageTotals;
  /** Distinct assistant messages counted (after dedupe + window filter). */
  messageCount: number;
}

/**
 * ENG-6314: sum assistant-turn token usage whose top-level `timestamp` falls
 * within [startMs, endMs] (inclusive), grouped by model plus an overall total.
 *
 * Used to attribute a workflow run's tokens by its `[started_at, finished_at]`
 * window — sound at the RUN grain (a run's window doesn't overlap itself), which
 * is why it's safe where per-phase windowing is not (concurrent phases overlap).
 * Apply it to the run's main session transcript AND each `subagents/agent-*.jsonl`
 * file (where the agent() fan-out spends most tokens), summing the results.
 *
 * Reuses the same defensive line parsing + message.id dedupe as
 * parseTranscriptUsage; a message with no parseable timestamp is skipped (it
 * can't be windowed). Bounds are compared as epoch ms so timezone spelling
 * differences ('Z' vs '+00:00') don't matter.
 */
export function sumTranscriptUsageInWindow(
  jsonl: string,
  startMs: number,
  endMs: number,
): WindowedUsageResult {
  interface Entry {
    tsMs: number;
    contributions: UsageContribution[];
  }
  const byId = new Map<string, Entry>();
  let syntheticCounter = 0;

  for (const line of jsonl.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let obj: unknown;
    try {
      obj = JSON.parse(trimmed);
    } catch {
      continue;
    }
    if (typeof obj !== 'object' || obj === null) continue;
    const record = obj as Record<string, unknown>;
    if (record.type !== 'assistant') continue;

    const message = record.message;
    if (typeof message !== 'object' || message === null) continue;
    const msg = message as Record<string, unknown>;
    const usage = msg.usage;
    if (typeof usage !== 'object' || usage === null) continue;
    const u = usage as Record<string, unknown>;

    const ts = record.timestamp;
    if (typeof ts !== 'string' || !ts) continue;
    const tsMs = new Date(ts).getTime();
    if (!Number.isFinite(tsMs)) continue;

    const id =
      typeof msg.id === 'string' && msg.id ? msg.id : `__noid_${syntheticCounter++}`;
    byId.set(id, { tsMs, contributions: usageContributions(msg, u) });
  }

  const byModel = new Map<string, TranscriptUsageTotals>();
  const totals = emptyTotals();
  let messageCount = 0;
  for (const e of byId.values()) {
    if (e.tsMs < startMs || e.tsMs > endMs) continue;
    // One message, however many models it spent on.
    messageCount++;
    for (const c of e.contributions) {
      const acc = byModel.get(c.model) ?? emptyTotals();
      acc.inputTokens += c.totals.inputTokens;
      acc.outputTokens += c.totals.outputTokens;
      acc.cacheCreationTokens += c.totals.cacheCreationTokens;
      acc.cacheReadTokens += c.totals.cacheReadTokens;
      byModel.set(c.model, acc);
      totals.inputTokens += c.totals.inputTokens;
      totals.outputTokens += c.totals.outputTokens;
      totals.cacheCreationTokens += c.totals.cacheCreationTokens;
      totals.cacheReadTokens += c.totals.cacheReadTokens;
    }
  }

  return { byModel, totals, messageCount };
}

/** True when the totals carry no tokens at all (nothing worth reporting). */
export function isEmptyTotals(totals: TranscriptUsageTotals): boolean {
  return (
    totals.inputTokens === 0 &&
    totals.outputTokens === 0 &&
    totals.cacheCreationTokens === 0 &&
    totals.cacheReadTokens === 0
  );
}

// ===========================================================================
// ENG-5566: per-turn attribution of usage to a run (boundary-marker delimited).
// ===========================================================================
//
// Under hybrid injection many tasks share one transcript, so per-task cost
// needs session-INTERNAL attribution. The manager (ENG-5565) writes an inert
// run marker (formatRunMarker) into the USER turn that opens each injected
// run. We walk the transcript in order: a marker user-turn sets the "current
// run"; the assistant turns that follow are attributed to it until the next
// marker. Assistant turns seen before any marker (ambient / interactive work)
// fall into the UNATTRIBUTED bucket (runId === null) so a task never absorbs
// turns that don't belong to it. The four token buckets stay separate so the
// downstream cost view can price fresh input/output vs cache distinctly.
//
// Dedupe matches parseTranscriptUsage: assistant usage is keyed by message.id
// (last write wins); the run in scope at the last occurrence is what sticks.

/** Usage attributed to a single (run, model) pair. */
export interface RunModelUsage {
  /** Run id from the boundary marker, or null for the unattributed bucket. */
  runId: string | null;
  model: string;
  totals: TranscriptUsageTotals;
}

export interface RunAttributionResult {
  /** Aggregated usage per (runId, model); runId null = unattributed bucket. */
  perRunModel: RunModelUsage[];
  /** Distinct run ids seen via markers, in first-seen order. */
  runIds: string[];
}

/** Extract the text of a user turn's `message.content` (string or block array). */
function userTurnText(message: Record<string, unknown>): string {
  const content = message.content;
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((block) => {
        if (block && typeof block === 'object') {
          const t = (block as Record<string, unknown>).text;
          if (typeof t === 'string') return t;
        }
        return '';
      })
      .join('\n');
  }
  return '';
}

/**
 * Attribute a transcript's assistant-turn token usage to runs, delimited by
 * the run markers the manager injects into user turns. Returns aggregated
 * totals per (runId, model); runId null is the unattributed/idle bucket.
 *
 * Same defensive posture as parseTranscriptUsage: malformed lines and missing
 * fields are skipped, never thrown.
 */
export function attributeTranscriptUsageByRun(jsonl: string): RunAttributionResult {
  interface Entry {
    runId: string | null;
    contributions: UsageContribution[];
  }
  // Dedupe assistant usage by message.id (last wins), capturing the run in
  // scope at that point.
  const byId = new Map<string, Entry>();
  const runIds = new Set<string>();
  let currentRunId: string | null = null;
  let syntheticCounter = 0;

  for (const line of jsonl.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    let obj: unknown;
    try {
      obj = JSON.parse(trimmed);
    } catch {
      continue;
    }
    if (typeof obj !== 'object' || obj === null) continue;
    const record = obj as Record<string, unknown>;

    const message = record.message;
    if (typeof message !== 'object' || message === null) continue;
    const msg = message as Record<string, unknown>;

    if (record.type === 'user') {
      // A run marker in a user turn opens (or switches) the attribution scope.
      const m = userTurnText(msg).match(RUN_MARKER_RE);
      if (m && m[1]) {
        currentRunId = m[1];
        runIds.add(m[1]);
      }
      continue;
    }

    if (record.type !== 'assistant') continue;
    const usage = msg.usage;
    if (typeof usage !== 'object' || usage === null) continue;
    const u = usage as Record<string, unknown>;
    const id =
      typeof msg.id === 'string' && msg.id ? msg.id : `__noid_${syntheticCounter++}`;
    byId.set(id, { runId: currentRunId, contributions: usageContributions(msg, u) });
  }

  // Aggregate per (runId, model). A space separator is used; a space can't appear in a uuid/model.
  const UNATTR = '\u0000unattributed';
  const agg = new Map<string, RunModelUsage>();
  for (const e of byId.values()) {
    // A model-bearing iteration is attributed to the SAME run as the turn that
    // made it — the advisor call is part of that run's work, not ambient.
    for (const c of e.contributions) {
      const key = `${e.runId ?? UNATTR}\u0000${c.model}`;
      const cur = agg.get(key);
      if (cur) {
        cur.totals.inputTokens += c.totals.inputTokens;
        cur.totals.outputTokens += c.totals.outputTokens;
        cur.totals.cacheCreationTokens += c.totals.cacheCreationTokens;
        cur.totals.cacheReadTokens += c.totals.cacheReadTokens;
      } else {
        agg.set(key, { runId: e.runId, model: c.model, totals: { ...c.totals } });
      }
    }
  }

  return { perRunModel: [...agg.values()], runIds: [...runIds] };
}
